import type { ControlDirection } from "@site-types/components/peopleScene"
import { useEffect, useRef } from "react"

const DENSITY = 50
const MAX_PEOPLE = 40
const TOO_CLOSE = 60
const MOVE_STEPS = 60
const STEP = TOO_CLOSE / MOVE_STEPS
const CONTROL_SPEED = 6
const FRAME_DURATION = 1000 / 60
const PERSON_WIDTH = 10
const PERSON_HEIGHT = 50
const OFFSCREEN_ARROW_MARGIN = 18
const DIRECTIONS = ["tl", "tr", "br", "bl"] as const
const MOVEMENTS = ["standing", "walking", "falling", "leveling"] as const

const CONTROL_KEYS: ReadonlyMap<string, ControlDirection> = new Map([
  ["arrowup", "up"],
  ["w", "up"],
  ["arrowdown", "down"],
  ["s", "down"],
  ["arrowleft", "left"],
  ["a", "left"],
  ["arrowright", "right"],
  ["d", "right"],
] as const)

function randomDirection() {
  return DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)]
}

function initialLevel() {
  const exponentialRandom = -Math.log(1 - Math.random())
  return Math.max(1, Math.min(Math.ceil(exponentialRandom * 0.8), 5))
}

function reducedMotionEnabled() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function removeMovementClasses(person: HTMLElement) {
  MOVEMENTS.forEach((className) => {
    person.classList.remove(className)
  })
}

function removeDirectionClasses(person: HTMLElement) {
  DIRECTIONS.forEach((className) => {
    person.classList.remove(className)
  })
}

function updateDirection(person: HTMLElement) {
  removeDirectionClasses(person)
  const targetTB = Number(person.offsetTop) > Number(person.dataset.top) ? "t" : "b"
  const targetLR = Number(person.offsetLeft) > Number(person.dataset.left) ? "l" : "r"
  person.classList.add(`${targetTB}${targetLR}`)
}

function setLevel(person: HTMLElement, newLevel: number) {
  person.dataset.level = newLevel.toString()
  ;[1, 2, 3, 4, 5].forEach((level) => {
    person.classList.remove(`level-${level}`)
  })
  person.classList.add(`level-${newLevel}`)
}

function walkTo(person: HTMLElement, xAdd = 0, yAdd = 0) {
  const targetX = person.offsetLeft + xAdd
  const targetY = person.offsetTop + yAdd
  person.dataset.left = targetX.toString()
  person.dataset.top = targetY.toString()
  updateDirection(person)
}

function editableEventTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.isContentEditable ||
    target.closest("input, textarea, select, [contenteditable='true'], [contenteditable='']")
  )
}

function controlKey(key: string) {
  return CONTROL_KEYS.get(key.toLowerCase())
}

function visibleViewportBounds() {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
  const sideMenu = document.querySelector<HTMLElement>("[data-id='site-side-menu']")
  const sideMenuRect = sideMenu?.getBoundingClientRect()
  const left = Math.max(0, Math.min(sideMenuRect?.right ?? 0, viewportWidth))

  return {
    bottom: viewportHeight,
    centerX: left + (viewportWidth - left) / 2,
    centerY: viewportHeight / 2,
    left,
    right: viewportWidth,
    top: 0,
  }
}

export default function PeopleScene() {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container || reducedMotionEnabled()) return

    let peopleCount = 0
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    let animationFrameId: number | undefined
    let cancelled = false
    const timeouts: ReturnType<typeof setTimeout>[] = []
    const pressedKeys = new Set<ControlDirection>()
    let selectedPerson: HTMLElement | null = null
    let hoveredPerson: HTMLElement | null = null
    const offscreenArrow = document.createElement("div")

    offscreenArrow.className = "people-offscreen-arrow"
    offscreenArrow.dataset.id = "people-offscreen-arrow"
    offscreenArrow.setAttribute("aria-hidden", "true")

    const scheduleTimeout = (callback: () => void, delay: number) => {
      const id = setTimeout(callback, delay)
      timeouts.push(id)
      return id
    }

    const hideOffscreenArrow = () => {
      offscreenArrow.classList.remove("is-visible")
    }

    const updateOffscreenArrow = () => {
      if (!selectedPerson || !container.contains(selectedPerson)) {
        hideOffscreenArrow()
        return
      }

      const centerX = selectedPerson.offsetLeft + selectedPerson.clientWidth / 2
      const centerY = selectedPerson.offsetTop + selectedPerson.clientHeight / 2
      const rect = container.getBoundingClientRect()
      const targetX = rect.left + centerX
      const targetY = rect.top + centerY
      const bounds = visibleViewportBounds()
      const isOutsideVisibleScreen =
        targetX < bounds.left || targetX > bounds.right || targetY < bounds.top || targetY > bounds.bottom

      if (!isOutsideVisibleScreen) {
        hideOffscreenArrow()
        return
      }

      const rawDeltaX = targetX - bounds.centerX
      const rawDeltaY = targetY - bounds.centerY
      const deltaX = rawDeltaX === 0 && rawDeltaY === 0 ? 0 : rawDeltaX
      const deltaY = rawDeltaX === 0 && rawDeltaY === 0 ? -1 : rawDeltaY
      const edgeHalfWidth = Math.max((bounds.right - bounds.left) / 2 - OFFSCREEN_ARROW_MARGIN, 1)
      const edgeHalfHeight = Math.max((bounds.bottom - bounds.top) / 2 - OFFSCREEN_ARROW_MARGIN, 1)
      const scale = Math.min(
        Math.abs(deltaX) > 0 ? edgeHalfWidth / Math.abs(deltaX) : Number.POSITIVE_INFINITY,
        Math.abs(deltaY) > 0 ? edgeHalfHeight / Math.abs(deltaY) : Number.POSITIVE_INFINITY
      )
      const arrowX = bounds.centerX + deltaX * scale
      const arrowY = bounds.centerY + deltaY * scale
      const angle = Math.atan2(deltaY, deltaX) + Math.PI / 2

      offscreenArrow.style.left = `${Math.max(bounds.left + OFFSCREEN_ARROW_MARGIN, Math.min(bounds.right - OFFSCREEN_ARROW_MARGIN, arrowX))}px`
      offscreenArrow.style.top = `${Math.max(bounds.top + OFFSCREEN_ARROW_MARGIN, Math.min(bounds.bottom - OFFSCREEN_ARROW_MARGIN, arrowY))}px`
      offscreenArrow.style.setProperty("--offscreen-arrow-angle", `${angle}rad`)
      offscreenArrow.classList.add("is-visible")
    }

    const setSelectedPerson = (person: HTMLElement | null) => {
      if (selectedPerson === person) return

      selectedPerson?.classList.remove("is-selected")
      selectedPerson = person
      pressedKeys.clear()

      if (!selectedPerson) {
        hideOffscreenArrow()
        return
      }

      selectedPerson.classList.add("is-selected")
      selectedPerson.dataset.left = selectedPerson.offsetLeft.toString()
      selectedPerson.dataset.top = selectedPerson.offsetTop.toString()
      selectedPerson.style.animationDelay = "0ms"
      removeMovementClasses(selectedPerson)
      selectedPerson.classList.add("standing")
      updateOffscreenArrow()
    }

    const completeFall = (id: number) => {
      const person = document.getElementById(`person-${id}`) as HTMLElement | null

      if (!person) return

      person.classList.remove("falling")
      person.style.animationDelay = "0ms"
      person.classList.add("standing")
    }

    const createPerson = (targetContainer: HTMLElement, index = 0) => {
      peopleCount += 1
      const id = peopleCount
      const width = targetContainer.clientWidth || 0
      const height = targetContainer.clientHeight || 0
      const top = Math.round(Math.random() * (height * 1.1) - PERSON_HEIGHT)
      const left = Math.round(Math.random() * (width * 1.1) - PERSON_WIDTH)
      const person = document.createElement("div")

      person.id = `person-${id}`
      person.classList.add("person", randomDirection(), "falling", Math.random() > 0.5 ? "male" : "female")
      person.addEventListener("mouseenter", () => {
        hoveredPerson = person
        person.classList.add("is-hovered")
      })

      person.addEventListener("mouseleave", () => {
        if (hoveredPerson === person) {
          hoveredPerson = null
          person.classList.remove("is-hovered")
        }
      })
      setLevel(person, initialLevel())
      person.dataset.top = top.toString()
      person.dataset.left = left.toString()
      person.style.top = `${top}px`
      person.style.left = `${left}px`
      person.style.zIndex = ((top + 100) * 100 + left).toString()
      person.style.animationDelay = `${id * 100}ms`

      targetContainer.appendChild(person)
      scheduleTimeout(() => completeFall(id), 2000 + index * 100)
    }

    const levelUp = (person: HTMLElement) => {
      const currentLevel = person.dataset.level ? Number(person.dataset.level) : 1

      if (currentLevel < 5) {
        const newLevel = currentLevel + 1
        removeMovementClasses(person)
        person.classList.add("leveling")

        scheduleTimeout(() => {
          removeMovementClasses(person)
          person.classList.add("standing")
          setLevel(person, newLevel)
        }, 1000)
        return
      }

      if (Math.random() < 0.2) {
        const xMultiplier = Math.random() > 0.5 ? 1 : -1
        walkTo(person, xMultiplier * 1000, -1000)

        scheduleTimeout(() => {
          if (hoveredPerson === person) {
            hoveredPerson = null
            person.classList.remove("is-hovered")
          }
          person.remove()
          if (!cancelled) createPerson(container)
        }, 10000)
      }
    }

    const moveAwayFromMouse = (event: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      const { top, bottom, left, right } = rect
      const { clientX, clientY } = event
      const mouseX = clientX - left
      const mouseY = clientY - top

      if (clientX <= left || clientX >= right || clientY <= top || clientY >= bottom) return

      const people = container.querySelectorAll<HTMLElement>(".person")

      people.forEach((person) => {
        if (person === selectedPerson || person === hoveredPerson) return

        const centerX = person.offsetLeft + person.clientWidth / 2
        const centerY = person.offsetTop + person.clientHeight / 2

        if (Math.abs(mouseX - centerX) >= TOO_CLOSE || Math.abs(mouseY - centerY) >= TOO_CLOSE) return

        const rightLeft = mouseX > centerX ? -1 : 1
        const topBottom = mouseY > centerY ? -1 : 1
        person.dataset.left = (person.offsetLeft + rightLeft * TOO_CLOSE).toString()
        person.dataset.top = (person.offsetTop + topBottom * TOO_CLOSE).toString()
        updateDirection(person)
      })
    }

    const updateAnimation = () => {
      const people = container.querySelectorAll<HTMLElement>(".person")
      const containerWidth = container.clientWidth || 0
      const containerHeight = container.clientHeight || 0

      people.forEach((person) => {
        let directionLR = Number(person.dataset.left) - person.offsetLeft
        let directionTB = Number(person.dataset.top) - person.offsetTop
        const falling = person.classList.contains("falling")
        let leveling = person.classList.contains("leveling")
        const selected = person === selectedPerson

        if (selected) {
          const horizontal = (pressedKeys.has("right") ? 1 : 0) - (pressedKeys.has("left") ? 1 : 0)
          const vertical = (pressedKeys.has("down") ? 1 : 0) - (pressedKeys.has("up") ? 1 : 0)
          const distance = Math.hypot(horizontal, vertical)
          const moveX = distance > 0 ? (horizontal / distance) * CONTROL_SPEED : 0
          const moveY = distance > 0 ? (vertical / distance) * CONTROL_SPEED : 0
          const nextLeft = person.offsetLeft + moveX
          const nextTop = person.offsetTop + moveY

          person.dataset.left = nextLeft.toString()
          person.dataset.top = nextTop.toString()
          directionLR = moveX
          directionTB = moveY

          if (moveX !== 0 || moveY !== 0) {
            updateDirection(person)
            person.style.left = `${nextLeft}px`
            person.style.top = `${nextTop}px`
          }

          person.style.zIndex = ((nextTop + 100) * 100 + nextLeft).toString()

          if (!falling && !leveling) {
            removeMovementClasses(person)
            person.classList.add(moveX === 0 && moveY === 0 ? "standing" : "walking")
          }

          return
        }

        if (!falling && !leveling && directionLR === 0 && directionTB === 0 && Math.random() < 0.01) {
          if (Math.random() < 0.1) {
            levelUp(person)
            leveling = true
          } else {
            const targetX =
              person.offsetLeft < -TOO_CLOSE
                ? person.offsetLeft + TOO_CLOSE
                : person.offsetLeft > containerWidth + TOO_CLOSE
                  ? person.offsetLeft - TOO_CLOSE
                  : Math.random() > 0.5
                    ? person.offsetLeft + TOO_CLOSE
                    : person.offsetLeft - TOO_CLOSE
            const targetY =
              person.offsetTop < -TOO_CLOSE
                ? person.offsetTop + TOO_CLOSE
                : person.offsetTop > containerHeight + TOO_CLOSE
                  ? person.offsetTop - TOO_CLOSE
                  : Math.random() > 0.5
                    ? person.offsetTop + TOO_CLOSE
                    : person.offsetTop - TOO_CLOSE

            walkTo(person, targetX, targetY)
            person.dataset.left = targetX.toString()
            person.dataset.top = targetY.toString()
            directionLR = Number(person.dataset.left) - person.offsetLeft
            directionTB = Number(person.dataset.top) - person.offsetTop
            updateDirection(person)
          }
        }

        let nextLeft = person.offsetLeft
        let nextTop = person.offsetTop

        if (directionLR > 0) nextLeft = person.offsetLeft + Math.min(directionLR, STEP)
        if (directionLR < 0) nextLeft = person.offsetLeft + Math.max(directionLR, -STEP)
        if (directionTB > 0) nextTop = person.offsetTop + Math.min(directionTB, STEP)
        if (directionTB < 0) nextTop = person.offsetTop + Math.max(directionTB, -STEP)

        if (directionLR !== 0) person.style.left = `${nextLeft}px`
        if (directionTB !== 0) person.style.top = `${nextTop}px`
        person.style.zIndex = ((nextTop + 100) * 100 + nextLeft).toString()

        if (!falling && !leveling) {
          removeMovementClasses(person)
          person.classList.add(directionLR === 0 && directionTB === 0 ? "standing" : "walking")
        }
      })

      updateOffscreenArrow()
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          animationFrameId = requestAnimationFrame(updateAnimation)
        }
      }, FRAME_DURATION)
    }

    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null
      const person = target?.closest(".person") as HTMLElement | null

      if (person && container.contains(person)) {
        setSelectedPerson(person)
        return
      }

      const rect = container.getBoundingClientRect()
      const insidePeopleContainer =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom

      if (insidePeopleContainer) setSelectedPerson(null)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (!selectedPerson) return

      if (event.key === "Escape") {
        setSelectedPerson(null)
        return
      }

      if (editableEventTarget(event.target)) return

      const key = controlKey(event.key)
      if (!key) return

      event.preventDefault()
      pressedKeys.add(key)
    }

    const onKeyUp = (event: KeyboardEvent) => {
      if (!selectedPerson || editableEventTarget(event.target)) return

      const key = controlKey(event.key)
      if (!key) return

      event.preventDefault()
      pressedKeys.delete(key)
    }

    const createPeople = () => {
      const width = container.clientWidth || 0
      const height = container.clientHeight || 0
      const count = Math.min(Math.round(DENSITY * ((width * height) / 1_000_000)), MAX_PEOPLE)

      container.appendChild(offscreenArrow)

      for (let index = 0; index < count; index += 1) {
        createPerson(container, index)
      }

      animationFrameId = requestAnimationFrame(updateAnimation)
    }

    document.addEventListener("mousemove", moveAwayFromMouse)
    document.addEventListener("click", onDocumentClick)
    document.addEventListener("keydown", onKeyDown)
    document.addEventListener("keyup", onKeyUp)
    createPeople()

    return () => {
      cancelled = true
      timeouts.forEach(clearTimeout)
      if (timeoutId) clearTimeout(timeoutId)
      if (animationFrameId) cancelAnimationFrame(animationFrameId)
      document.removeEventListener("mousemove", moveAwayFromMouse)
      document.removeEventListener("click", onDocumentClick)
      document.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("keyup", onKeyUp)
      pressedKeys.clear()
      selectedPerson = null
      hoveredPerson = null
      container.replaceChildren()
    }
  }, [])

  return (
    <div
      aria-hidden="true"
      className="motion-reduce:[&_.people-offscreen-arrow::before]:animation-none motion-reduce:[&_.people-offscreen-arrow::after]:animation-none pointer-events-none absolute inset-0 z-[3] block h-full w-full overflow-visible [&_.people-offscreen-arrow.is-visible]:block [&_.people-offscreen-arrow::after]:absolute [&_.people-offscreen-arrow::after]:inset-[3px] [&_.people-offscreen-arrow::after]:block [&_.people-offscreen-arrow::after]:bg-[var(--deep-blue)] [&_.people-offscreen-arrow::after]:content-[''] [&_.people-offscreen-arrow::after]:[animation:people-offscreen-arrow-nudge_900ms_ease-in-out_infinite] [&_.people-offscreen-arrow::after]:[clip-path:polygon(50%_0%,100%_100%,50%_76%,0%_100%)] [&_.people-offscreen-arrow::before]:absolute [&_.people-offscreen-arrow::before]:inset-0 [&_.people-offscreen-arrow::before]:block [&_.people-offscreen-arrow::before]:bg-[var(--white)] [&_.people-offscreen-arrow::before]:content-[''] [&_.people-offscreen-arrow::before]:[animation:people-offscreen-arrow-nudge_900ms_ease-in-out_infinite] [&_.people-offscreen-arrow::before]:[clip-path:polygon(50%_0%,100%_100%,50%_76%,0%_100%)] [&_.people-offscreen-arrow]:pointer-events-none [&_.people-offscreen-arrow]:fixed [&_.people-offscreen-arrow]:top-[18px] [&_.people-offscreen-arrow]:left-[18px] [&_.people-offscreen-arrow]:z-20 [&_.people-offscreen-arrow]:hidden [&_.people-offscreen-arrow]:h-[26px] [&_.people-offscreen-arrow]:w-[26px] [&_.people-offscreen-arrow]:-translate-x-1/2 [&_.people-offscreen-arrow]:-translate-y-1/2 [&_.people-offscreen-arrow]:rotate-[var(--offscreen-arrow-angle,0rad)] [&_.person.bl]:[--y-offset:-768px] [&_.person.br]:[--y-offset:-512px] [&_.person.falling]:opacity-0 [&_.person.falling]:[--person-color:green] [&_.person.falling]:[animation:person-falling_2000ms_ease-in_1] [&_.person.falling]:[background-position:0_calc(var(--y-offset,0px)-128px)] [&_.person.is-hovered]:opacity-100 [&_.person.is-selected]:opacity-100 [&_.person.level-1.female]:bg-[url('/images/careers-sprites/female-L1-sprite-sheet.png')] [&_.person.level-1.male]:bg-[url('/images/careers-sprites/male-L1-sprite-sheet.png')] [&_.person.level-2.female]:bg-[url('/images/careers-sprites/female-L2-sprite-sheet.png')] [&_.person.level-2.male]:bg-[url('/images/careers-sprites/male-L2-sprite-sheet.png')] [&_.person.level-3.female]:bg-[url('/images/careers-sprites/female-L3-sprite-sheet.png')] [&_.person.level-3.male]:bg-[url('/images/careers-sprites/male-L3-sprite-sheet.png')] [&_.person.level-4.female]:bg-[url('/images/careers-sprites/female-L4-sprite-sheet.png')] [&_.person.level-4.male]:bg-[url('/images/careers-sprites/male-L4-sprite-sheet.png')] [&_.person.level-5.female]:bg-[url('/images/careers-sprites/female-L5-sprite-sheet.png')] [&_.person.level-5.male]:bg-[url('/images/careers-sprites/male-L5-sprite-sheet.png')] [&_.person.leveling::after]:absolute [&_.person.leveling::after]:top-[-10px] [&_.person.leveling::after]:left-[calc(50%-15px)] [&_.person.leveling::after]:block [&_.person.leveling::after]:h-[30px] [&_.person.leveling::after]:w-[30px] [&_.person.leveling::after]:rounded-[5px] [&_.person.leveling::after]:bg-white [&_.person.leveling::after]:text-center [&_.person.leveling::after]:font-bold [&_.person.leveling::after]:text-[var(--deep-blue)] [&_.person.leveling::after]:leading-[30px] [&_.person.leveling::after]:content-['+1'] [&_.person.leveling::after]:[animation:levelup-plus_1500ms_ease-out_1] [&_.person.leveling]:z-[1] [&_.person.leveling]:[animation:person-levelup_400ms_steps(2)_infinite] [&_.person.leveling]:[background-position:-512px_calc(var(--y-offset)-128px)] [&_.person.standing]:[animation:person-standing_1300ms_steps(2)_infinite] [&_.person.standing]:[background-position:0_var(--y-offset)] [&_.person.tl]:[--y-offset:0px] [&_.person.tr]:[--y-offset:-256px] [&_.person.walking]:[animation:person-walking_400ms_steps(6)_infinite] [&_.person.walking]:[background-position:-256px_var(--y-offset)] [&_.person::before]:absolute [&_.person::before]:top-0 [&_.person::before]:left-[15px] [&_.person::before]:block [&_.person::before]:h-[50px] [&_.person::before]:w-[20px] [&_.person::before]:translate-x-[50px] [&_.person::before]:translate-y-[30px] [&_.person::before]:rotate-45 [&_.person::before]:rounded-full [&_.person::before]:opacity-50 [&_.person::before]:content-[''] [&_.person:hover]:opacity-100 [&_.person]:pointer-events-auto [&_.person]:absolute [&_.person]:top-0 [&_.person]:left-0 [&_.person]:block [&_.person]:h-[128px] [&_.person]:w-[128px] [&_.person]:cursor-pointer [&_.person]:text-center [&_.person]:text-[1.6em] [&_.person]:text-black [&_.person]:leading-[50px] [&_.person]:opacity-25 [&_.person]:[--person-color:var(--cyan)] [&_.person]:[--person-standing-end:128px] [&_.person]:[--person-standing-start:0px] [&_.person]:[--person-walking-end:896px] [&_.person]:[--person-walking-start:256px] [&_.person]:[background-position:0_0]"
      id="people-container"
      data-id="people-container"
      ref={containerRef}
    />
  )
}
